// Map a web-app WorkoutNode type to the firmware "display type" the hardware
// follower renders (colour + fallback title). Mirror of the API-side mapping in
// `api/lib/translatePlan.js` (TYPE_MAP + isStructuralRoundRest) — keep the two in
// sync. The frontend needs its own copy because `app/` and `api/` are separate
// packages with no shared import path.
//
// The device only accepts the five firmware phase types below; anything else is
// dropped on the wire (see `api/sockets/liveRunner.js` DISPLAY_TYPES) and the
// server falls back to a structural Work/Rest guess. So this returns one of the
// five valid types, or `undefined` to let that server fallback stand (e.g. an
// AMRAP `Group` item, which has no leaf phase type).

const TYPE_MAP = {
  Work: "Work",
  Rest: "Rest",
  Transition: "Rest",
  SetRest: "RndRest",
  Warmup: "Warm",
  Cooldown: "Cool",
};

const FIRMWARE_TYPES = new Set(["Work", "Rest", "RndRest", "Warm", "Cool"]);

// A `Rest` that is the last child of a multi-round Group is a round boundary —
// firmware `RndRest`. The web plan no longer carries a distinct `SetRest` type,
// so round-rest semantics live in the tree structure.
function isStructuralRoundRest(node, parent, indexInParent) {
  if (!node || node.type !== "Rest" || !parent || parent.type !== "Group") {
    return false;
  }
  const rounds = Number(parent.value);
  if (!Number.isFinite(rounds) || rounds <= 1) return false;
  if (!Array.isArray(parent.children)) return false;
  return indexInParent === parent.children.length - 1;
}

// Returns a firmware display type (Work|Rest|RndRest|Warm|Cool) or undefined.
export function firmwareDisplayType(node, parent, indexInParent) {
  if (!node) return undefined;
  const mapped = isStructuralRoundRest(node, parent, indexInParent)
    ? "RndRest"
    : TYPE_MAP[node.type];
  return FIRMWARE_TYPES.has(mapped) ? mapped : undefined;
}
